import dayjs, {
    ConfigType,
    Dayjs,
    isDayjs,
    ManipulateType,
    OpUnitType,
} from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import es from "dayjs/locale/es";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.locale(es);
dayjs.extend(customParseFormat);

interface DateUtilsOptions {
    withTimezone?: boolean;
    inclusive?: boolean;
}

const defaultOpts = {
    withTimezone: true,
    inclusive: true,
};

export class DayjsUtils {
    static today(opts: DateUtilsOptions = defaultOpts) {
        if (opts.withTimezone) {
            return dayjs.tz(new Date(), "America/Lima");
        }
        return dayjs.utc(new Date());
    }

    static getDate(date: ConfigType, withTimezone = true) {
        if (isDayjs(date)) {
            if (withTimezone) {
                return dayjs.tz(date, "America/Lima");
            }
            return date;
        }
        if (withTimezone) {
            return dayjs.tz(date, "America/Lima");
        }
        return dayjs.utc(date);
    }

    static isBefore(
        date1: Dayjs | string,
        date2: Dayjs | string,
        unit: OpUnitType = "days",
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);
        return dt1.isBefore(dt2, unit);
    }

    static isAfter(
        date1: Dayjs | string,
        date2: Dayjs | string,
        unit: OpUnitType = "days",
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);
        return dt1.isAfter(dt2, unit);
    }

    static isUntilTo(
        date: Dayjs,
        count: number,
        unit: ManipulateType = "days",
        opts: DateUtilsOptions = defaultOpts
    ): boolean {
        const dt = DayjsUtils.getDate(date, opts.withTimezone);
        const threeMonthsAgo = dayjs().subtract(count, unit);
        return dt.isAfter(threeMonthsAgo);
    }

    static isSame(
        date1: Dayjs | string,
        date2: Dayjs | string,
        unit: OpUnitType = "days",
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);
        return dt1.isSame(dt2, unit);
    }

    static isSameOrBefore(
        date1: Dayjs,
        date2: Dayjs,
        unit: OpUnitType = "days",
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);
        return dt1.isSameOrBefore(dt2, unit);
    }

    static isSameOrAfter(
        date1: Dayjs | string,
        date2: Dayjs | string,
        unit: OpUnitType = "days",
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);
        return dt1.isSameOrAfter(dt2, unit);
    }

    static isPast(
        date: Dayjs | string,
        opts: DateUtilsOptions = defaultOpts
    ): boolean {
        const today = dayjs(new Date());
        const dt = DayjsUtils.getDate(date, opts.withTimezone);
        return opts.inclusive ? dt.isSameOrBefore(today) : dt.isBefore(today);
    }

    static isFuture(
        date: Dayjs | string,
        opts: DateUtilsOptions = defaultOpts
    ): boolean {
        const today = dayjs(new Date());
        const dt = DayjsUtils.getDate(date, opts.withTimezone);
        return opts.inclusive ? dt.isSameOrAfter(today) : dt.isAfter(today);
    }

    static diff(
        date1: Dayjs | string,
        date2: Dayjs | string,
        unit: OpUnitType = "days",
        float?: boolean,
        opts: DateUtilsOptions = defaultOpts
    ) {
        const dt1 = DayjsUtils.getDate(date1, opts.withTimezone);
        const dt2 = DayjsUtils.getDate(date2, opts.withTimezone);

        return dt1.diff(dt2, unit, float);
    }

    static isPastUntil(
        date: Dayjs | string,
        numberOfDays: number,
        opts: DateUtilsOptions = defaultOpts
    ): boolean {
        const _date = DayjsUtils.getDate(date, opts.withTimezone);
        const _now = dayjs.utc();
        const diff = _now.diff(_date, "days");
        return _date <= _now && diff < numberOfDays;
    }

    static fromFormat(value: string, format: string) {
        return dayjs.tz(dayjs(value, format), "America/Lima");
    }

    static getFullDay(date: Date) {
        const _day = date.getDate();
        const formatDay = _day < 10 ? `0${_day}` : _day;
        const _month = date.getMonth() + 1;
        const formatMonth = _month < 10 ? `0${_month}` : _month;
        const _year = date.getFullYear();
        return `${formatDay}/${formatMonth}/${_year}`;
    }
    static getHourAMPM(date: Date) {
        const _hours = date.getHours();
        const _minutes = date.getMinutes();
        const ampm = _hours >= 12 ? "pm" : "am";
        const newHour = _hours % 12 ? _hours % 12 : 12;
        const newMinutes = _minutes < 10 ? "0" + _minutes : _minutes;
        return `${newHour}:${newMinutes} ${ampm}`;
    }
}
